/**
 * Shared utilities for resolving employee custom field values from OpenSearch
 * documents. Used by both programs-table and programs-aggregate-table components.
 */

export const EMPLOYEE_CUSTOM_ALIASES: Record<string, string[]> = {
  // Only keep true semantic aliases. Type-suffix fallbacks are handled by
  // employeeCustomLeafFallbackKey + stripped leaf candidate matching.
  employeeBirthDate: ['employeeDob'],
  employeeDateOfBirth: ['employeeDob'],
  employeeBirth: ['employeeDob'],
};

export function camelToSnakeCase(value: string) {
  return value.replace(/([A-Z])/g, (match) => `_${match.toLowerCase()}`);
}

export function employeeCustomLeafFallbackKey(leaf: string) {
  return leaf.replace(
    /(KeywordAnalyzable|Analyzable|Keyword|Boolean|Integer|Nested|Object|Float|Date|Text)$/,
    ''
  );
}

function withoutEmployeePrefix(value: string) {
  if (!value) {
    return value;
  }

  if (value.startsWith('employee_')) {
    return value.slice('employee_'.length);
  }

  if (value.startsWith('employee') && value.length > 'employee'.length) {
    const tail = value.slice('employee'.length);
    return tail.charAt(0).toLowerCase() + tail.slice(1);
  }

  return value;
}

function normalizeCandidate(value: string) {
  return value.replace(/^_+/, '');
}

export function employeeCustomFieldValue(
  customFields: Record<string, unknown> | undefined,
  leaf: string,
  aliases: Record<string, string[]> = EMPLOYEE_CUSTOM_ALIASES
): unknown {
  const strippedLeaf = employeeCustomLeafFallbackKey(leaf);
  const aliasCandidates = [...(aliases[leaf] || []), ...(aliases[strippedLeaf] || [])];

  const candidates = Array.from(new Set([leaf, strippedLeaf, ...aliasCandidates]));

  for (const candidate of candidates) {
    const strippedCandidate = employeeCustomLeafFallbackKey(candidate);
    const noEmployeeCandidate = withoutEmployeePrefix(candidate);
    const strippedNoEmployeeCandidate = employeeCustomLeafFallbackKey(noEmployeeCandidate);
    const baseVariants = [
      candidate,
      strippedCandidate,
      noEmployeeCandidate,
      strippedNoEmployeeCandidate,
    ];
    const keyVariants = Array.from(
      new Set([
        ...baseVariants.map(normalizeCandidate),
        ...baseVariants.map((value) => normalizeCandidate(camelToSnakeCase(value))),
      ])
    ).filter(Boolean);

    for (const key of keyVariants) {
      const value = customFields?.[key];
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }
  }

  return undefined;
}
